import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext'; // Import useCart
import SizeChartModal from './SizeChartModal'; // Import the new modal

// Simple SVG Icons for Carousel Arrows (can be replaced with better ones or icon library)
const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const ProductCard = ({ product }) => {
  const { addItemToCart } = useCart(); // Use the useCart hook
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(
    (product.category === 'Tee' || product.category === 'Hoodie') && product.variants && product.variants.length > 0 
      ? product.variants[0].size 
      : null
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [addedToCartMessage, setAddedToCartMessage] = useState('');
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false); // State for size chart modal

  const handleQuantityChange = (amount) => {
    setQuantity(prev => Math.max(1, prev + amount));
    if (addedToCartMessage) setAddedToCartMessage('');
  };

  const handleSizeChange = (event) => {
    setSelectedSize(event.target.value);
    if (addedToCartMessage) setAddedToCartMessage('');
  };

  const handleAddToCart = () => {
    let variantToAddToCart;
    if (product.category === 'Tee' || product.category === 'Hoodie') {
      if (!selectedSize) {
        setAddedToCartMessage('Please select a size.');
        setTimeout(() => setAddedToCartMessage(''), 2000);
        return;
      }
      variantToAddToCart = product.variants.find(v => v.size === selectedSize);
    } else {
      variantToAddToCart = { 
        id: product.variantId,
        size: null, 
        price: product.price 
      };
    }

    if (!variantToAddToCart || !variantToAddToCart.id) {
        console.error('Could not determine variant to add to cart for:', product.name, product);
        setAddedToCartMessage('Error adding item.');
        setTimeout(() => setAddedToCartMessage(''), 2000);
        return;
    }

    addItemToCart(product, quantity, variantToAddToCart);
    setAddedToCartMessage('Added to cart!');
    setTimeout(() => setAddedToCartMessage(''), 2000);
  };

  let displayPrice = product.price;
  if ((product.category === 'Tee' || product.category === 'Hoodie') && selectedSize) {
    const variant = product.variants.find(v => v.size === selectedSize);
    if (variant) {
      displayPrice = variant.price;
    }
  }

  useEffect(() => {
    setCurrentImageIndex(0);
    if ((product.category === 'Tee' || product.category === 'Hoodie') && product.variants && product.variants.length > 0) {
        setSelectedSize(product.variants[0].size);
    } else {
        setSelectedSize(null);
    }
    setAddedToCartMessage('');
    setIsSizeChartOpen(false); // Close size chart if product changes
  }, [product]);

  // Generalized nextImage function for any product with an images array
  const nextImage = () => {
    if (product.images && product.images.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product.images && product.images.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex - 1 + product.images.length) % product.images.length);
    }
  };
  
  // Determine which image to show (for cart context or simple display)
  // If multiple images, use the first one. Otherwise, use the single image prop.
  const primaryImageForCart = product.images && product.images.length > 0 ? product.images[0] : product.image;

  return (
    <>
      <div className="bg-white/40 backdrop-blur-lg rounded-xl shadow-2xl overflow-hidden p-4 sm:p-6 flex flex-col h-full">
        {/* Image Carousel Area - Now generalized */}
        <div className="relative w-full aspect-square mb-4 bg-white/20 rounded-lg flex items-center justify-center overflow-hidden" style={{ maxHeight: '75vh'}}>
          {product.images && product.images.length > 0 ? (
            <>
              <img 
                src={product.images[currentImageIndex]} 
                alt={`${product.name} - view ${currentImageIndex + 1}`}
                className="object-contain w-full h-full"
              />
              {product.images.length > 1 && ( // Show controls only if more than one image
                <>
                  {/* Left Arrow */}
                  <button 
                    onClick={prevImage} 
                    className="absolute top-1/2 left-2 sm:left-3 transform -translate-y-1/2 bg-white/50 hover:bg-white/80 text-gray-800 p-2 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors z-10"
                    aria-label="Previous image"
                  >
                    <ChevronLeftIcon />
                  </button>
                  
                  {/* Right Arrow */}
                  <button 
                    onClick={nextImage} 
                    className="absolute top-1/2 right-2 sm:right-3 transform -translate-y-1/2 bg-white/50 hover:bg-white/80 text-gray-800 p-2 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors z-10"
                    aria-label="Next image"
                  >
                    <ChevronRightIcon />
                  </button>
                  
                  {/* Indicator Dots */}
                  <div className="absolute bottom-2 sm:bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-1.5 sm:space-x-2 z-10">
                    {product.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full shadow hover:bg-gray-400 focus:outline-none transition-colors ${currentImageIndex === index ? 'bg-gray-700' : 'bg-gray-300'}`}
                        aria-label={`Go to image ${index + 1}`}
                        aria-current={currentImageIndex === index}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : product.image ? ( // Fallback for products with a single `product.image` defined (legacy or specific case)
            <img 
              src={product.image} 
              alt={product.name} 
              className="object-contain w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">No Image Available</div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-grow flex flex-col">
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1 sm:mb-2">{product.name}</h3>
          <p className="text-gray-700 text-xs sm:text-sm mb-2 sm:mb-3 flex-grow whitespace-pre-line">{product.description}</p>
          <p className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">${displayPrice.toFixed(2)}</p>

          {(product.category === 'Tee' || product.category === 'Hoodie') && product.variants && product.variants.length > 0 && (
            <div className="mb-3 sm:mb-4">
              <div className="flex justify-between items-center mb-1">
                <label htmlFor={`size-${product.id}`} className="block text-xs sm:text-sm font-medium text-gray-700">Size:</label>
                <button onClick={() => setIsSizeChartOpen(true)} className="text-xs text-indigo-600 hover:text-indigo-800 inline-block underline">Size Chart</button>
              </div>
              <select 
                id={`size-${product.id}`} 
                value={selectedSize || ''}
                onChange={handleSizeChange}
                className="w-full p-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
              >
                {product.variants.map(variant => (
                  <option key={variant.size} value={variant.size}>{variant.size}</option>
                ))}
              </select>
            </div>
          )}

          {/* Action Row: Quantity Selector and Add to Cart Button */}
          <div className="mt-auto">
            <div className="flex items-stretch space-x-3 mb-3 sm:mb-4">
              {/* Quantity Selector */}
              <div className="flex items-center border border-gray-300 rounded-md">
                <button 
                  onClick={() => handleQuantityChange(-1)} 
                  className="px-2.5 sm:px-3 py-1 sm:py-1.5 text-base sm:text-lg font-medium text-gray-700 hover:bg-gray-100 rounded-l-md focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  -
                </button>
                <input 
                    type="number"
                    id={`quantity-${product.id}`}
                    value={quantity} 
                    readOnly 
                    className="w-10 sm:w-12 text-center p-1 sm:p-1.5 text-xs sm:text-sm border-l border-r bg-transparent border-gray-300 text-gray-900 focus:outline-none"
                    aria-live="polite"
                />
                <button 
                  onClick={() => handleQuantityChange(1)} 
                  className="px-2.5 sm:px-3 py-1 sm:py-1.5 text-base sm:text-lg font-medium text-gray-700 hover:bg-gray-100 rounded-r-md focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              {/* Add to Cart Button */}
              <button 
                onClick={handleAddToCart}
                className="flex-grow bg-gray-800 hover:bg-gray-700 active:bg-gray-900 text-white font-semibold py-2.5 sm:py-3 px-4 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-colors duration-150 text-sm sm:text-base"
              >
                Add to Cart
              </button>
            </div>
            {addedToCartMessage && (
              <p className="text-center text-sm text-green-600 font-medium h-5">{addedToCartMessage}</p>
            )}
          </div>
        </div>
      </div>
      {isSizeChartOpen && (product.category === 'Tee' || product.category === 'Hoodie') && (
        <SizeChartModal onClose={() => setIsSizeChartOpen(false)} />
      )}
    </>
  );
};

export default ProductCard; 